// The frame every screen renders inside: dark sidebar, top bar, content column.
//
// It is also where "who is signed in" becomes visible. The three auth states
// get three different frames on purpose (§05): "checking" is not "signed out",
// and a signed-out visitor gets a sign-in prompt rather than a dashboard whose
// every panel fails with `unauthorized` — that failure mode tells a person
// nothing about what to do next.

import { Link, Outlet, useRouter, useRouterState } from "@tanstack/react-router";
import { Fragment, useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Currency, type FxRate } from "@crm/shared";
import styles from "./AppShell.module.css";
import { Button, LinkButton } from "../design/Button.js";
import { Spinner } from "../design/Feedback.js";
import { Segmented, type SegmentedOption } from "../design/Segmented.js";
import { fallbackChrome, useDeclaredChrome } from "../design/pageChrome.js";
import { todayIsoDate } from "../format/calendarDate.js";
import { formatRate } from "../format/money.js";
import {
  linkedReportCurrency,
  PATHS_OWNING_CURRENCY_PARAM,
  useReportCurrency,
  useSetReportCurrency,
} from "../format/reportCurrency.js";
import { useAuth } from "../auth/useAuth.js";
import { useAutoSignIn } from "../auth/autoSignIn.js";
import { LOGOUT_URL } from "../auth/session.js";
import { useFxRates } from "../api/queries.js";
import { GlobalSearch } from "./GlobalSearch.js";

/**
 * `to` is a literal union so a typo is a compile error rather than a nav item
 * that silently routes nowhere — TanStack Router types it from the route tree.
 *
 * Icons are text glyphs, not an icon package: the design draws them this way,
 * and eight characters is not worth a dependency that ships a few hundred.
 */
const NAV_GROUPS = [
  // No heading: the overview sits above every group.
  {
    heading: null,
    items: [{ to: "/", label: "Dashboard", icon: "◧" }],
  },
  // First, because it is the work that recurs every month, in the order it
  // happens: see what is due, close the month, bill the milestones, raise and
  // chase the documents.
  {
    heading: "Billing",
    items: [
      { to: "/calendar", label: "Calendar", icon: "▦" },
      { to: "/billing/close", label: "To bill", icon: "▣" },
      { to: "/implementation", label: "Milestones", icon: "◆" },
      { to: "/invoices", label: "Invoices", icon: "▤" },
    ],
  },
  // Winning and tracking deals: how sales is going, the pipeline, pricing one
  // not yet signed, the partners it comes through, and what was said about it.
  {
    heading: "Sales",
    items: [
      // Signed ARR, pipeline, bookings and the partners side by side, as a board reads them.
      { to: "/sales", label: "Performance", icon: "◭" },
      { to: "/deals", label: "Deals", icon: "▤" },
      { to: "/pricing", label: "Pricing", icon: "⊕" },
      { to: "/partners", label: "Partners", icon: "◈" },
      // What was SAID about a deal, as against what it bills. Its own screen
      // because the inbox of unfiled notes belongs to no deal in particular.
      { to: "/notes", label: "Notes", icon: "✎" },
      { to: "/map", label: "Map", icon: "◉" },
    ],
  },
  // How the business is doing, looking back — and, first, the one that looks
  // forward: real revenue run on, with estimates for deals not yet contracted.
  {
    heading: "Reports",
    items: [
      { to: "/projections", label: "Projections", icon: "◬" },
      { to: "/margin", label: "Margin", icon: "◩" },
      { to: "/trends", label: "Trends", icon: "◫" },
      { to: "/regions", label: "Regions", icon: "◍" },
      // What the whole platform cost us, by deployment, and the tenants no deal
      // covers: fleet-wide, because those tenants have no deal to reach it through.
      { to: "/usage", label: "Platform costs", icon: "◎" },
    ],
  },
  // What the arithmetic reads rather than where work is recorded: the price
  // of a unit and the rate a currency converts at.
  {
    heading: "Reference",
    items: [
      { to: "/rates", label: "Rate card", icon: "⊟" },
      { to: "/fx-rates", label: "FX rates", icon: "⊞" },
    ],
  },
] as const;

/** Every nav item, for the rules that must see the whole sidebar at once. */
type NavItem = (typeof NAV_GROUPS)[number]["items"][number];
const ALL_NAV_ITEMS: readonly NavItem[] = NAV_GROUPS.flatMap(
  (group): readonly NavItem[] => group.items,
);

/**
 * Whether a nav item may only light on its OWN path.
 *
 * TanStack matches a link by prefix, which is what keeps Deals lit while you are
 * on /deals/$dealId — right for a detail page, wrong the moment a SECOND nav
 * item lives under the first: /billing/close lit Billing and To bill at once,
 * and a sidebar with two things selected cannot say where you are.
 *
 * Derived from the list rather than hand-flagged on the parent, so nesting a new
 * screen under an existing one cannot leave its parent lit behind it.
 */
export function matchesExactly(to: string): boolean {
  // The root is a prefix of every path there is, so it is always exact.
  if (to === "/") return true;
  return ALL_NAV_ITEMS.some((item) => item.to !== to && item.to.startsWith(`${to}/`));
}

const SIDEBAR_COLLAPSED_KEY = "crm.sidebar.collapsed";

/**
 * Whether the sidebar is a rail of icons or a full column, remembered per
 * browser. Read once at mount and written on every toggle; a browser that
 * refuses storage (a private window, a hardened profile) simply forgets, which
 * is the fallback the rest of this shell can live with.
 */
function useSidebarCollapsed(): [boolean, () => void] {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true";
    } catch {
      return false;
    }
  });
  const toggle = useCallback((): void => {
    setCollapsed((previous) => {
      const next = !previous;
      try {
        window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
      } catch {
        // Nothing to do: the preference lives for this page and no longer.
      }
      return next;
    });
  }, []);
  return [collapsed, toggle];
}

function initials(displayName: string): string {
  return displayName
    .split(/\s+/)
    .filter((part) => part !== "")
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}

/**
 * The rate in force today: the latest row whose effective_date has arrived.
 * Informational only — every report pins to its OWN period's rate server-side
 * and says which one it used, which is the number anyone reconciles against.
 */
function inForceRate(rates: readonly FxRate[]): FxRate | null {
  const today = todayIsoDate();
  let best: FxRate | null = null;
  for (const rate of rates) {
    if (rate.effective_date > today) continue;
    if (best === null || rate.effective_date > best.effective_date) best = rate;
  }
  return best;
}

function FxChip(): ReactNode {
  const rates = useFxRates();
  const rate = useMemo(() => inForceRate(rates.data?.items ?? []), [rates.data]);

  if (rate === null) return null;

  return (
    <div className={styles.fxChip} title={`In force since ${rate.effective_date}`}>
      <span>USD/CAD</span>
      <span className={`${styles.fxValue} mono`}>{formatRate(rate.usd_to_cad)}</span>
    </div>
  );
}

const REPORT_CURRENCIES: ReadonlyArray<SegmentedOption<Currency>> = Currency.options.map(
  (currency) => ({
    value: currency,
    label: currency,
  }),
);

/**
 * The one switch for every screen that adds up money across deals. A deal or
 * an invoice keeps its own currency whatever this says; see
 * `format/reportCurrency.tsx`.
 */
function ReportCurrencySwitch(): ReactNode {
  const currency = useReportCurrency();
  const setCurrency = useSetReportCurrency();
  return (
    <div title="The currency totals across deals are shown in. A deal or an invoice keeps its own.">
      <Segmented
        label="Report currency"
        options={REPORT_CURRENCIES}
        value={currency}
        onChange={setCurrency}
        variant="inset"
      />
    </div>
  );
}

/**
 * A link that names a currency (`?currency=USD`, as every report's link used
 * to carry) switches the reader to it, and the param is then taken out of the
 * URL. Left in, it would contradict the switch the moment the reader used it.
 */
function useAdoptLinkedReportCurrency(): void {
  const router = useRouter();
  const location = useRouterState({ select: (state) => state.location });
  const setCurrency = useSetReportCurrency();
  useEffect(() => {
    if (PATHS_OWNING_CURRENCY_PARAM.has(location.pathname)) return;
    const search = location.search as Record<string, unknown>;
    if (!("currency" in search)) return;
    const { currency: linked, ...rest } = search;
    // The provider already started in it on a landing link (App.tsx); this is
    // for a link followed inside the app, and to remember the choice.
    const adopted = linkedReportCurrency({ pathname: location.pathname, search });
    if (adopted !== null) setCurrency(adopted);
    const hash = location.hash === "" ? "" : `#${location.hash}`;
    router.history.replace(`${location.pathname}${router.options.stringifySearch(rest)}${hash}`);
  }, [location, router, setCurrency]);
}

function SidebarIdentity({ collapsed }: { collapsed: boolean }): ReactNode {
  const auth = useAuth();

  switch (auth.status) {
    case "loading":
      return (
        <div className={styles.identity}>
          <Spinner label="Checking your session…" inline />
        </div>
      );

    case "anonymous":
      return (
        <div className={styles.identity}>
          <div className={styles.status}>
            <span className={styles.dot} />
            <span>Signed out</span>
          </div>
          {/* A real navigation, not a fetch: the login endpoint redirects to
              Keycloak, and an XHR would follow that into a CORS wall. */}
          <LinkButton href={auth.signInUrl} small>
            Sign in
          </LinkButton>
        </div>
      );

    case "authenticated":
      return (
        <div className={styles.identity}>
          <div className={styles.identityRow}>
            {/* In the rail the initials are all that is left of the name, so
                the name moves into the tooltip rather than disappearing. */}
            <span
              className={styles.avatar}
              aria-hidden="true"
              {...(collapsed ? { title: auth.principal.display_name } : {})}
            >
              {initials(auth.principal.display_name)}
            </span>
            <span className={styles.identityText}>
              <span className={styles.identityName}>{auth.principal.display_name}</span>
              <span className={styles.identityRoles}>{auth.principal.roles.join(" · ")}</span>
            </span>
            {auth.principal.actor_type === "agent" && (
              <span className={styles.agentBadge}>agent</span>
            )}
          </div>
          <div className={styles.identityRow}>
            {/* Authenticated is the one state in which the API has demonstrably
                answered 200 — so it is the only one that may claim it is live. */}
            <span className={styles.status} {...(collapsed ? { title: "JSON API live" } : {})}>
              <span className={`${styles.dot} ${styles.dotLive}`} />
              <span className={styles.statusLabel}>JSON API live</span>
            </span>
            {/* A real navigation, exactly like the sign-in link above, and for
                the mirror-image reason: the logout endpoint redirects to
                Keycloak, and only the browser can surrender the SSO cookie that
                actually keeps the user signed in. A fetch would follow that 302
                into a CORS wall and leave them signed in. */}
            <a className={styles.signOut} href={LOGOUT_URL} aria-label="Sign out" title="Sign out">
              {collapsed ? "⎋" : "Sign out"}
            </a>
          </div>
        </div>
      );
  }
}

function SignedOutGate({ reason, signInUrl }: { reason: string; signInUrl: string }): ReactNode {
  return (
    <div className={styles.gate}>
      <h2 className={styles.gateTitle}>Sign in to see the CRM</h2>
      <p className={styles.gateBody}>{reason}</p>
      <p className={styles.gateBody}>
        Access is granted through the <code>crm-users</code> group in Keycloak. Signing in here
        starts that flow; nothing is stored in this browser but an opaque session cookie.
      </p>
      <LinkButton href={signInUrl}>Sign in</LinkButton>
    </div>
  );
}

export function AppShell(): ReactNode {
  const auth = useAuth();
  // Anonymous is a detour, not a screen: this starts the trip to Keycloak and
  // reports whether one is under way, so the gate below is only ever shown to
  // someone who is genuinely stuck here.
  const signingIn = useAutoSignIn(auth);
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const declared = useDeclaredChrome();
  const chrome = declared ?? fallbackChrome(pathname);
  const [collapsed, toggleCollapsed] = useSidebarCollapsed();
  useAdoptLinkedReportCurrency();

  return (
    <div className={styles.root}>
      <aside className={`${styles.sidebar} ${collapsed ? styles.collapsed : ""}`}>
        <div className={styles.brand}>
          {/* The hamburger, top-left where every app keeps it. The same three
              bars in both states: it is the control that resizes the sidebar,
              and it does not need to describe the state it will produce. */}
          <button
            type="button"
            className={styles.hamburger}
            onClick={toggleCollapsed}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-expanded={!collapsed}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <span className={styles.bar} aria-hidden="true" />
            <span className={styles.bar} aria-hidden="true" />
            <span className={styles.bar} aria-hidden="true" />
          </button>
          {/* The full Dial AI wordmark, the same file the main app uses. It IS
              the name, so the alt text is the name and nothing else. In the
              rail there is no room for it and the hamburger stands alone. */}
          {!collapsed && <img className={styles.logo} src="/DialAI.svg" alt="Dial AI" />}
        </div>

        <nav className={styles.nav} aria-label="Sections">
          {NAV_GROUPS.map((group) => (
            <Fragment key={group.heading ?? "top"}>
              {group.heading !== null && <div className={styles.navSection}>{group.heading}</div>}
              {group.items.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={styles.navItem}
                  activeProps={{ className: `${styles.navItem} ${styles.navItemActive}` }}
                  activeOptions={{ exact: matchesExactly(item.to) }}
                  // The label is hidden in the rail, so it moves into the tooltip.
                  {...(collapsed ? { title: item.label } : {})}
                >
                  <span className={styles.navIcon} aria-hidden="true">
                    {item.icon}
                  </span>
                  <span className={styles.navLabel}>{item.label}</span>
                </Link>
              ))}
            </Fragment>
          ))}
        </nav>

        <SidebarIdentity collapsed={collapsed} />
      </aside>

      <main className={styles.main}>
        <header className={styles.topbar}>
          <h1 className={styles.screenTitle}>{chrome.title}</h1>
          {/* Counted from the rows the screen loaded, so it keeps its whole
              width while the subtitle beside it gives way. */}
          {chrome.summary !== undefined && (
            <span className={styles.screenSummary}>{chrome.summary}</span>
          )}
          {/* Centred on the bar, not on the gap between the title and the
              controls — see .topbarCenter. Only once signed in: the box would
              401 on every keystroke otherwise, and a search that always fails
              is worse than none. */}
          {auth.status === "authenticated" && (
            <div className={styles.topbarCenter}>
              <GlobalSearch />
            </div>
          )}
          <div className={styles.topbarRight}>
            {auth.status === "authenticated" && <ReportCurrencySwitch />}
            {auth.status === "authenticated" && <FxChip />}
            {chrome.onExport !== undefined && (
              <Button small onClick={chrome.onExport} title={chrome.exportLabel}>
                Export CSV
              </Button>
            )}
          </div>
        </header>

        <div className={styles.content}>
          {auth.status === "loading" ? (
            <Spinner label="Checking your session…" />
          ) : signingIn ? (
            <Spinner label="Taking you to sign in…" />
          ) : auth.status === "anonymous" ? (
            <SignedOutGate reason={auth.reason} signInUrl={auth.signInUrl} />
          ) : (
            <Outlet />
          )}
        </div>
      </main>
    </div>
  );
}
